import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Shield, AlertTriangle, ExternalLink, Heart, 
  CreditCard, Bot, Scale, FileText, ArrowLeft, Cookie, Info
} from 'lucide-react';

export default function DisclaimerPage() {
  return (
    <div className="min-h-screen bg-muted/30 py-12" data-testid="disclaimer-page">
      <SEO 
        title="Disclaimer & Advertising Policy"
        description="All American Dog Club disclaimer, advertising policy, and cookie information. Learn about our advertising partners and data practices."
        url="/disclaimer"
      />
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-3xl font-heading font-medium mb-2">Disclaimer</h1>
          <p className="text-muted-foreground">
            Important information about using WorldPets and All-American Dog Club services
          </p>
        </div>

        <div className="space-y-6">
          {/* General Disclaimer */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="w-5 h-5 text-primary" />
                General Disclaimer
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-muted-foreground">
              <p>
                The information provided on WorldPets Global and All-American Dog Club websites, 
                mobile applications, and associated services (collectively, "the Platform") is 
                provided "as is" without any representations or warranties, express or implied.
              </p>
              <p>
                We make no representations or warranties in relation to the accuracy, completeness, 
                or suitability of the information and materials found or offered on the Platform for 
                any particular purpose. You acknowledge that such information and materials may contain 
                inaccuracies or errors and we expressly exclude liability for any such inaccuracies or 
                errors to the fullest extent permitted by law.
              </p>
              <p>
                Your use of any information or materials on the Platform is entirely at your own risk, 
                for which we shall not be liable. It shall be your own responsibility to ensure that 
                any products, services, or information available through the Platform meet your specific 
                requirements.
              </p>
            </CardContent>
          </Card>

          {/* Not Professional Advice */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                Not Professional Veterinary or Medical Advice
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-muted-foreground">
              <p>
                <strong>The Platform does not provide veterinary, medical, or professional advice.</strong> 
                All content related to pet health, nutrition, training, breeding, or behavior is provided 
                for informational purposes only and should not be construed as professional advice.
              </p>
              <p>
                Always consult with a qualified veterinarian, animal behaviorist, or other appropriate 
                professional before making decisions about your pet's health, diet, training, or well-being. 
                Never disregard professional veterinary advice or delay in seeking it because of something 
                you have read on this Platform.
              </p>
              <p>
                In case of a pet emergency, contact your veterinarian or local emergency animal hospital 
                immediately.
              </p>
            </CardContent>
          </Card>

          {/* User-Generated Content */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                User-Generated Content
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-muted-foreground">
              <p>
                The Platform allows users to post, share, and exchange information, opinions, and content 
                ("User Content"). We do not endorse, support, represent, or guarantee the completeness, 
                truthfulness, accuracy, or reliability of any User Content.
              </p>
              <p>
                You understand that by using the Platform, you may be exposed to User Content that might 
                be offensive, harmful, inaccurate, or otherwise inappropriate. Under no circumstances will 
                we be liable in any way for any User Content, including any errors or omissions, or any 
                loss or damage of any kind incurred as a result of the use of any User Content.
              </p>
              <p>
                We reserve the right, but are not obligated, to remove or modify User Content for any 
                reason, including User Content that we believe violates our Terms of Service or policies.
              </p>
            </CardContent>
          </Card>

          {/* External Links */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-green-500" />
                External Links & Third-Party Content
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-muted-foreground">
              <p>
                The Platform may contain links to third-party websites, content, or resources that are 
                not owned or controlled by us. We have no control over, and assume no responsibility for, 
                the content, privacy policies, or practices of any third-party websites or services.
              </p>
              <p>
                <strong>Exception for Official Partners:</strong> Links to our official sponsors, partners, 
                and verified affiliates are provided with our endorsement. These partnerships are clearly 
                identified where applicable.
              </p>
              <p>
                We strongly advise you to read the terms and conditions and privacy policies of any 
                third-party websites or services that you visit. Your interactions with third-party 
                websites are solely between you and such third parties.
              </p>
            </CardContent>
          </Card>

          {/* Affiliate & Ad Disclosure */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Affiliate & Advertising Disclosure
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-muted-foreground">
              <p>
                The Platform may display advertisements and contain affiliate links. This means we may 
                earn a commission if you click on certain links and make a purchase or sign up for a 
                service. These affiliate relationships do not influence our editorial content or the 
                information we provide.
              </p>
              <p>
                Advertisements displayed on the Platform are provided by third-party advertising networks. 
                We do not endorse and are not responsible for the content of any advertisements or the 
                products/services they promote.
              </p>
              <p>
                We strive to only partner with reputable companies and services, but we encourage you to 
                conduct your own research before making any purchasing decisions based on advertisements 
                or affiliate content on our Platform.
              </p>
            </CardContent>
          </Card>

          {/* Google AdSense Disclosure */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-500" />
                Google AdSense & Personalized Advertising
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-muted-foreground">
              <Alert className="mb-4 bg-blue-50 border-blue-200">
                <Info className="w-4 h-4" />
                <AlertDescription>
                  This site uses Google AdSense to display advertisements. We are committed to transparency 
                  about how advertising works on our platform.
                </AlertDescription>
              </Alert>
              
              <h4 className="font-semibold text-foreground">How Google Advertising Works</h4>
              <ul>
                <li>
                  <strong>Personalized Ads:</strong> Google may use cookies and similar technologies 
                  to serve ads based on your prior visits to our website or other websites on the Internet.
                </li>
                <li>
                  <strong>DoubleClick Cookie:</strong> Google's use of the DoubleClick cookie enables it 
                  and its partners to serve ads based on your visit to our site and/or other sites.
                </li>
                <li>
                  <strong>Third-Party Vendors:</strong> Third-party vendors, including Google, use cookies 
                  to serve ads based on your prior visits to our website or other websites.
                </li>
              </ul>
              
              <h4 className="font-semibold text-foreground mt-4">Your Choices</h4>
              <p>
                You can opt out of personalized advertising by visiting{' '}
                <a 
                  href="https://www.google.com/settings/ads" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Google Ads Settings
                  <ExternalLink className="w-3 h-3 inline ml-1" />
                </a>. You may also opt out of certain third-party vendor's use of cookies for personalized 
                advertising by visiting{' '}
                <a 
                  href="https://www.aboutads.info/choices/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  www.aboutads.info
                  <ExternalLink className="w-3 h-3 inline ml-1" />
                </a>.
              </p>
            </CardContent>
          </Card>

          {/* Cookie Policy */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="w-5 h-5 text-orange-500" />
                Cookie Policy
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-muted-foreground">
              <p>
                We use cookies and similar tracking technologies to enhance your experience on our Platform. 
                By using our Platform, you consent to our use of cookies in accordance with this policy.
              </p>
              
              <h4 className="font-semibold text-foreground mt-4">Types of Cookies We Use</h4>
              <div className="grid md:grid-cols-2 gap-3 mt-2">
                <div className="p-3 bg-muted rounded-lg">
                  <h5 className="font-medium text-sm text-foreground">Essential Cookies</h5>
                  <p className="text-xs mt-1">
                    Required for authentication, security, and basic site functionality.
                  </p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <h5 className="font-medium text-sm text-foreground">Preference Cookies</h5>
                  <p className="text-xs mt-1">
                    Remember your settings like language, theme, and display preferences.
                  </p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <h5 className="font-medium text-sm text-foreground">Analytics Cookies</h5>
                  <p className="text-xs mt-1">
                    Help us understand how visitors interact with our site to improve experience.
                  </p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <h5 className="font-medium text-sm text-foreground">Advertising Cookies</h5>
                  <p className="text-xs mt-1">
                    Used by Google AdSense and partners to deliver relevant advertisements.
                  </p>
                </div>
              </div>
              
              <h4 className="font-semibold text-foreground mt-4">Managing Cookies</h4>
              <p>
                Most web browsers allow you to control cookies through their settings. However, limiting 
                cookies may impact your experience on our Platform. To learn more about cookies, visit{' '}
                <a 
                  href="https://www.aboutcookies.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  aboutcookies.org
                  <ExternalLink className="w-3 h-3 inline ml-1" />
                </a>.
              </p>
            </CardContent>
          </Card>

          {/* AI Features Disclaimer */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-purple-500" />
                AI-Powered Features Disclaimer
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-muted-foreground">
              <p>
                Certain features on the Platform may utilize artificial intelligence (AI) and machine 
                learning technologies, including but not limited to health detection suggestions, pet 
                matching recommendations, and content translation.
              </p>
              <p>
                <strong>AI-generated content and suggestions are provided for informational purposes only</strong> 
                and should not be relied upon as the sole basis for any decisions regarding your pet's 
                health, breeding, training, or care. AI systems can make errors and may not account for 
                all relevant factors in your specific situation.
              </p>
              <p>
                Always verify AI-generated suggestions with qualified professionals. We are not liable 
                for any outcomes resulting from reliance on AI-powered features.
              </p>
            </CardContent>
          </Card>

          {/* Payments & Membership */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-indigo-500" />
                Payments & Membership Terms
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-muted-foreground">
              <p>
                All payments processed through the Platform are handled by Stripe, a third-party payment 
                processor. We do not store or have access to your full payment card details. Payment 
                processing is subject to Stripe's terms of service and privacy policy.
              </p>
              <p>
                Membership fees are billed according to the plan selected at signup. Refunds may be 
                available according to our refund policy, which can be found in our Terms of Service. 
                Membership benefits are subject to change, and we will provide notice of any material 
                changes.
              </p>
              <p>
                Marketplace transactions between users are conducted at the users' own risk. While we 
                provide tools to facilitate these transactions, we are not a party to any sale and do 
                not guarantee the quality, safety, or legality of items listed.
              </p>
            </CardContent>
          </Card>

          {/* Limitation of Liability */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-slate-500" />
                Limitation of Liability
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-muted-foreground">
              <p>
                To the maximum extent permitted by applicable law, in no event shall WorldPets Global, 
                All-American Dog Club, their affiliates, directors, employees, agents, or licensors be 
                liable for any indirect, punitive, incidental, special, consequential, or exemplary 
                damages, including without limitation damages for loss of profits, goodwill, use, data, 
                or other intangible losses.
              </p>
              <p>
                Our total liability to you for any claims arising from or related to the Platform shall 
                not exceed the greater of (a) the amounts you paid to us in the twelve (12) months prior 
                to the claim, or (b) one hundred US dollars ($100).
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">
                If you have any questions about this Disclaimer, please contact us at{' '}
                <a 
                  href="mailto:support@americandogclub.com" 
                  className="text-primary hover:underline"
                >
                  support@americandogclub.com
                </a>
              </p>
              <p className="text-xs text-muted-foreground mt-4">
                Last Updated: April 2026
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
